import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './useAuth';
import ResultPublishButton from './ResultPublishButton';
import { buildScheduleClipCalendarUrl } from './oneTapSchedule';
import ClipThumbnail from './ClipThumbnail';
import './ProjectsPage.css';

async function downloadClip(url, filename, onProgress) {
  const response = await fetch(url, {
    headers: { Accept: 'video/mp4, video/*, */*' },
  });
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const contentLength = Number(response.headers.get('Content-Length')) || 0;
  if (!response.body || !contentLength) {
    onProgress?.(100);
    const blob = await response.blob();
    return blob;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress?.(Math.min(Math.round((received / contentLength) * 100), 99));
  }

  onProgress?.(100);
  return new Blob(chunks, { type: 'video/mp4' });
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function ProjectsPage() {
  const { fileStem } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, getFreshToken } = useAuth();
  const getFreshTokenRef = useRef(getFreshToken);
  getFreshTokenRef.current = getFreshToken;

  const [project, setProject] = useState(null);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [showPauseButton, setShowPauseButton] = useState({});
  const [sharingFiles, setSharingFiles] = useState(new Set());
  const [downloadProgressByFile, setDownloadProgressByFile] = useState({});
  const videoRefs = useRef({});

  const isPremiumUser = user?.plan_tier === 'premium';

  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return undefined;
    }
    if (!fileStem) return undefined;

    let cancelled = false;

    async function loadProject() {
      setLoading(true);
      setError('');
      try {
        const token = await getFreshTokenRef.current();
        if (!token) {
          if (!cancelled) setError('Please sign in to view your projects.');
          return;
        }
        const res = await fetch(`/api/user/projects/${encodeURIComponent(fileStem)}/clips`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (cancelled) return;
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Could not load project');
        }
        const data = await res.json();
        setProject({ file_stem: data.file_stem, file_name: data.file_name });
        setClips(data.clips || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load project');
          setClips([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadProject();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, fileStem, navigate]);

  const showStatus = (message) => {
    setStatusMessage(message);
    window.setTimeout(() => setStatusMessage(null), 4000);
  };

  const handleDownload = async (clip) => {
    const { url, filename } = clip;
    try {
      setDownloadProgressByFile((prev) => ({ ...prev, [filename]: 0 }));
      showStatus({ type: 'info', message: `Downloading ${filename}…` });
      const blob = await downloadClip(url, filename, (progress) => {
        setDownloadProgressByFile((prev) => ({ ...prev, [filename]: progress }));
      });
      triggerBlobDownload(blob, filename);
      showStatus({ type: 'success', message: `${filename} downloaded successfully.` });
    } catch (err) {
      showStatus({ type: 'error', message: err.message || 'Download failed.' });
    } finally {
      setDownloadProgressByFile((prev) => {
        const next = { ...prev };
        delete next[filename];
        return next;
      });
    }
  };

  const handleShare = async (clip) => {
    const { url, filename } = clip;

    const copyLinkFallback = async () => {
      try {
        await navigator.clipboard.writeText(window.location.origin + url);
        showStatus({ type: 'success', message: 'Download link copied — paste it to share your video.' });
      } catch {
        showStatus({ type: 'error', message: 'Could not share or copy the link. Try Download instead.' });
      }
    };

    if (!navigator.share) {
      await copyLinkFallback();
      return;
    }

    setSharingFiles((prev) => new Set([...prev, filename]));
    showStatus({ type: 'info', message: `Preparing ${filename} to share…` });

    try {
      const response = await fetch(url, {
        headers: { Accept: 'video/mp4, video/*, */*' },
      });
      if (!response.ok) {
        throw new Error(`Could not load video: ${response.status}`);
      }
      const blob = await response.blob();
      const mime = blob.type?.startsWith('video/') ? blob.type : 'video/mp4';
      const file = new File([blob], filename, { type: mime });
      await navigator.share({ files: [file], title: filename });
      showStatus({ type: 'success', message: 'Shared successfully.' });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        await copyLinkFallback();
      }
    } finally {
      setSharingFiles((prev) => {
        const next = new Set(prev);
        next.delete(filename);
        return next;
      });
    }
  };

  const handleSchedule = (clip) => {
    navigate(buildScheduleClipCalendarUrl({
      fileStem,
      clipFilename: clip.filename,
    }));
  };

  const displayName = (project?.file_name || fileStem || '').replace(/\.[^.]+$/, '');

  return (
    <div className="projects-page">
      <div className="projects-page__shell">
        <header className="projects-page__header">
          <Link to="/" className="projects-page__back">
            <span className="projects-page__back-chevron" aria-hidden="true">&lt;</span>
            <span className="projects-page__back-label">Dashboard</span>
          </Link>
          <h1 className="projects-page__title">{displayName || 'Project'}</h1>
          <p className="projects-page__subtitle">
            {clips.length > 0
              ? `${clips.length} clip${clips.length === 1 ? '' : 's'} from this raw video`
              : 'Clips generated from your raw video'}
          </p>
        </header>

        {statusMessage && (
          <p className={`projects-page__status projects-page__status--${statusMessage.type}`}>
            {statusMessage.message}
          </p>
        )}

        {loading && <p className="projects-page__status">Loading clips…</p>}

        {!loading && error && (
          <div className="projects-page__empty">
            <p>{error}</p>
            <Link to="/" className="projects-page__cta">Go to dashboard</Link>
          </div>
        )}

        {!loading && !error && clips.length === 0 && (
          <div className="projects-page__empty">
            <p>No clips found for this project.</p>
            <Link to="/" className="projects-page__cta">Upload a new video</Link>
          </div>
        )}

        {!loading && !error && clips.length > 0 && (
          <div className="projects-page__clips">
            <div className="results-grid">
              {clips.map((clip, index) => {
                const isPlaying = playingVideo === clip.filename;
                return (
                  <div key={clip.filename} className="result-item">
                    <div className="result-item__body">
                      <div className="result-thumbnail-container">
                        {isPlaying ? (
                          <div className="result-video-wrapper">
                            <video
                              ref={(el) => {
                                videoRefs.current[clip.filename] = el;
                                if (el && isPlaying) {
                                  el.play().catch(() => {});
                                }
                              }}
                              src={clip.url}
                              className="result-video-player"
                              onEnded={() => {
                                setPlayingVideo(null);
                                setShowPauseButton((prev) => ({ ...prev, [clip.filename]: false }));
                              }}
                              onClick={() => {
                                setShowPauseButton((prev) => ({
                                  ...prev,
                                  [clip.filename]: !prev[clip.filename],
                                }));
                                window.setTimeout(() => {
                                  setShowPauseButton((prev) => ({ ...prev, [clip.filename]: false }));
                                }, 2000);
                              }}
                              loop={false}
                              playsInline
                              controls={false}
                            />
                            {showPauseButton[clip.filename] && (
                              <button
                                type="button"
                                className="result-pause-button"
                                onClick={() => {
                                  if (videoRefs.current[clip.filename]) {
                                    videoRefs.current[clip.filename].pause();
                                  }
                                  setPlayingVideo(null);
                                  setShowPauseButton((prev) => ({ ...prev, [clip.filename]: false }));
                                }}
                                aria-label="Pause video"
                              >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <ClipThumbnail
                              fileStem={fileStem}
                              clipFilename={clip.filename}
                              getFreshTokenRef={getFreshTokenRef}
                            />
                            <button
                              type="button"
                              className="result-play-button"
                              onClick={() => {
                                setPlayingVideo(clip.filename);
                                setShowPauseButton((prev) => ({ ...prev, [clip.filename]: true }));
                              }}
                              aria-label={`Play ${clip.filename}`}
                            >
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                      <div className="result-content">
                        <div className="result-rank">#{index + 1}</div>
                        <div className="result-filename-wrap">
                          <h4 className="result-filename" title={clip.filename}>
                            {clip.filename}
                          </h4>
                        </div>
                        <div className="result-actions">
                          <div className="result-actions-row">
                            <button
                              type="button"
                              className={`share-btn ${sharingFiles.has(clip.filename) ? 'loading' : ''}`}
                              onClick={() => handleShare(clip)}
                              disabled={
                                sharingFiles.has(clip.filename) ||
                                clip.filename in downloadProgressByFile
                              }
                              aria-label={
                                sharingFiles.has(clip.filename)
                                  ? 'Preparing video to share'
                                  : 'Share video'
                              }
                              aria-busy={sharingFiles.has(clip.filename)}
                            >
                              {sharingFiles.has(clip.filename) ? (
                                <span className="download-spinner" aria-hidden="true">⏳</span>
                              ) : (
                                <svg className="share-btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="url(#result-action-grad)" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
                                </svg>
                              )}
                            </button>
                            <button
                              type="button"
                              className={`download-btn ${clip.filename in downloadProgressByFile ? 'loading' : ''}`}
                              onClick={() => handleDownload(clip)}
                              disabled={
                                clip.filename in downloadProgressByFile ||
                                sharingFiles.has(clip.filename)
                              }
                              aria-busy={clip.filename in downloadProgressByFile}
                              aria-label={
                                clip.filename in downloadProgressByFile
                                  ? `Downloading ${downloadProgressByFile[clip.filename]} percent`
                                  : 'Download video'
                              }
                            >
                              {clip.filename in downloadProgressByFile
                                ? `${downloadProgressByFile[clip.filename]}%`
                                : 'DOWNLOAD'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="result-item__ribbon result-item__ribbon--split">
                      <button
                        type="button"
                        className="result-schedule-bar"
                        onClick={() => handleSchedule(clip)}
                        aria-label={`Schedule ${clip.filename}`}
                      >
                        <span className="result-schedule-bar__label">Schedule</span>
                      </button>
                      <ResultPublishButton
                        fileStem={fileStem}
                        clipFilename={clip.filename}
                        userId={user?.uid || ''}
                        isPremiumUser={isPremiumUser}
                        getAuthToken={getFreshToken}
                        hideIcons
                        onStatusMessage={(message) => {
                          if (message) {
                            showStatus({ type: 'info', message });
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
